import { CheckCircle } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, FormControl, InputLabel, MenuItem, SelectChangeEvent, Tab } from "@mui/material";
import { displayDateAndTime, displayOutcomeStatus, FormSection, INTERNAL_USER_NAME } from "Backend";
import DebouncedTextarea from "components/DebouncedTextarea";
import LoadingSpinner from "components/LoadingSpinner";
import PSelect from "components/PSelect";
import { OutcomeStatus, SectionValueView, ValidationStatus } from "generated/graphql";
import { fullName } from "helpers/name-helpers";
import { FC, ReactNode, SyntheticEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { updateSectionValueOutcome, updateSectionValueValidation } from "redux/slices/case";
import { selectLensOutcomeStatusConfiguration } from "redux/slices/clientLens";
import { selectUserById } from "redux/slices/client";
import theme from "theme";
import { P } from "theme/typography";
import { stripNull, stripNullEmpty } from "helpers/coll";

export type SectionOutcomeTab = "review" | "qa";

interface SectionOutcomeProps {
  caseId: number;
  formId: number;
  section: FormSection;
  sectionValue?: SectionValueView | undefined;
  tab: SectionOutcomeTab;
}

const SavedState = ({ saved, loading }: { saved: boolean; loading: boolean }) => {
  const [showSaved, setShowSaved] = useState<boolean>(false);

  useEffect(() => {
    if (saved) setShowSaved(true);
  }, [saved]);

  return loading ? (
    <LoadingSpinner size={18} />
  ) : showSaved ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}>
      <CheckCircle sx={{ mr: 1 }} color="success" />
      <P variant="body4">Updated</P>
    </Box>
  ) : (
    <></>
  );
};

const Commentary = ({ textarea }: { textarea: ReactNode }) => {
  return (
    <div className="mt-4">
      <p className="font-bold text-xs mb-2">Commentary</p>
      <div className="rounded-md border border-gray-100 p-2 text-sm">{textarea}</div>
    </div>
  );
};

const LastEdited = ({ by, at }: { by?: string; at?: string }) => {
  return (
    <Box
      sx={{
        borderRadius: `${theme.shape.borderRadius}px`,
        border: `1px solid ${theme.palette.grey[100]}`,
        padding: 2,
        display: "flex",
        flexDirection: "column",
        mt: 3,
      }}>
      <P variant="body4" sx={{ color: "grey.300" }}>
        Last edited by:
      </P>
      <P variant="body4" sx={{ color: "grey.300" }}>
        {by && at && `${by} ${displayDateAndTime(new Date(at))}`}
      </P>
    </Box>
  );
};

function AutoCommentary({ text }: { text: string }) {
  return (
    <Box sx={{ mt: 2 }}>
      <P variant="body3" sx={{ mb: 1 }}>
        Auto Commentary
      </P>
      <Box
        sx={{
          borderRadius: `${theme.shape.borderRadius}px`,
          backgroundColor: theme.palette.grey[50],
          padding: 0.5,
          maxHeight: 250,
          overflowY: "auto",
        }}>
        <pre style={{ whiteSpace: "pre-wrap" }}>
          <P variant="sm" sx={{ color: theme.palette.grey[900] }}>
            {text}
          </P>
        </pre>
      </Box>
    </Box>
  );
}

interface OutcomeReviewProps extends Omit<SectionOutcomeProps, "tab"> {}

const OutcomeReview = ({ caseId, formId, section, sectionValue }: OutcomeReviewProps) => {
  const dispatch = useAppDispatch();
  const outcomeUser = useAppSelector((state) => selectUserById(state, sectionValue?.outcomeUpdatedBy || -1));
  const [outcomeStatus, setOutcomeStatus] = useState<OutcomeStatus>(
    sectionValue?.outcomeStatus ?? OutcomeStatus.Unreviewed
  );
  const [outcomeComment, setOutcomeComment] = useState<string>(sectionValue?.outcomeComment ?? "");
  const outcomeAutoComment = stripNullEmpty(sectionValue?.outcomeAutoComment);
  const { lensOutcomeStatusConfigurations, sbrEnabled } = useAppSelector(selectLensOutcomeStatusConfiguration);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const status = sectionValue?.outcomeStatus;
    const comment = sectionValue?.outcomeComment;

    if (status !== undefined) setOutcomeStatus(status);
    if (comment !== null && comment !== undefined) setOutcomeComment(comment);
  }, [sectionValue]);

  const updateOutcome = (outcomeStatus: OutcomeStatus, outcomeComment: string): void => {
    setLoading(true);
    dispatch(
      updateSectionValueOutcome({
        sectionId: section.id,
        caseId,
        formId,
        outcomeStatus,
        outcomeComment,
      })
    )
      .unwrap()
      .then(() => {
        setSaved(true);
        setLoading(false);
      });
  };

  const handleOutcomeStatusChange = (event: SelectChangeEvent<unknown>) => {
    const outcomeStatus = event.target.value as OutcomeStatus;

    setOutcomeStatus(outcomeStatus);
    updateOutcome(outcomeStatus, outcomeComment);
  };

  const handleOutcomeCommentChange = (comment: string) => {
    setOutcomeComment(comment);
    updateOutcome(outcomeStatus, comment);
  };

  const outcomeStatusDisplaySX = (outcomeStatus: OutcomeStatus): "block" | "none" => {
    return lensOutcomeStatusConfigurations.find((config) => config.outcomeStatus === outcomeStatus)?.isHidden
      ? "none"
      : "block";
  };

  return (
    <TabPanel value="review">
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <FormControl>
          <InputLabel>Overall Outcome</InputLabel>
          <PSelect label="Overall Outcome" value={outcomeStatus} onChange={handleOutcomeStatusChange}>
            <MenuItem value={OutcomeStatus.Unreviewed}>
              {displayOutcomeStatus(OutcomeStatus.Unreviewed, sbrEnabled, lensOutcomeStatusConfigurations)}
            </MenuItem>
            <MenuItem
              value={OutcomeStatus.DiscrepantInformation}
              sx={{
                display: outcomeStatusDisplaySX(OutcomeStatus.DiscrepantInformation),
              }}>
              {displayOutcomeStatus(OutcomeStatus.DiscrepantInformation, sbrEnabled, lensOutcomeStatusConfigurations)}
            </MenuItem>
            <MenuItem
              value={OutcomeStatus.MissingInformation}
              sx={{
                display: outcomeStatusDisplaySX(OutcomeStatus.MissingInformation),
              }}>
              {displayOutcomeStatus(OutcomeStatus.MissingInformation, sbrEnabled, lensOutcomeStatusConfigurations)}
            </MenuItem>
            <MenuItem
              value={OutcomeStatus.NotApplicable}
              sx={{
                display: outcomeStatusDisplaySX(OutcomeStatus.NotApplicable),
              }}>
              {displayOutcomeStatus(OutcomeStatus.NotApplicable, sbrEnabled, lensOutcomeStatusConfigurations)}
            </MenuItem>
            <MenuItem
              value={OutcomeStatus.PendingRecords}
              sx={{
                display: outcomeStatusDisplaySX(OutcomeStatus.PendingRecords),
              }}>
              {displayOutcomeStatus(OutcomeStatus.PendingRecords, sbrEnabled, lensOutcomeStatusConfigurations)}
            </MenuItem>
            <MenuItem
              value={OutcomeStatus.Rejected}
              sx={{
                display: outcomeStatusDisplaySX(OutcomeStatus.Rejected),
              }}>
              {displayOutcomeStatus(OutcomeStatus.Rejected, sbrEnabled, lensOutcomeStatusConfigurations)}
            </MenuItem>
            <MenuItem
              value={OutcomeStatus.Valid}
              sx={{
                display: outcomeStatusDisplaySX(OutcomeStatus.Valid),
              }}>
              {displayOutcomeStatus(OutcomeStatus.Valid, sbrEnabled, lensOutcomeStatusConfigurations)}
            </MenuItem>
          </PSelect>
        </FormControl>
        {outcomeAutoComment !== undefined && <AutoCommentary text={outcomeAutoComment} />}
        <Commentary
          textarea={
            <DebouncedTextarea
              minRows={1}
              textareaProps={{
                placeholder: "Write comments about the section...",
              }}
              value={outcomeComment}
              onChange={handleOutcomeCommentChange}
            />
          }
        />
      </Box>
      <LastEdited
        by={outcomeUser !== undefined ? fullName(outcomeUser) : INTERNAL_USER_NAME}
        at={stripNull(sectionValue?.outcomeUpdatedAt)}
      />

      <Box sx={{ mt: 3, display: "flex" }}>
        <SavedState saved={saved} loading={loading} />
      </Box>
    </TabPanel>
  );
};

const OutcomeQA: FC<Omit<SectionOutcomeProps, "tab">> = ({ caseId, formId, section, sectionValue }) => {
  const dispatch = useAppDispatch();
  const qaUser = useAppSelector((state) => selectUserById(state, sectionValue?.validationUpdatedBy || -1));
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>(
    sectionValue?.validationStatus ?? ValidationStatus.Unvalidated
  );
  const [validationComment, setValidationComment] = useState<string>(sectionValue?.validationComment ?? "");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const status = sectionValue?.validationStatus;
    const comment = sectionValue?.validationComment;

    if (status !== undefined) setValidationStatus(status);
    if (comment !== null && comment !== undefined) setValidationComment(comment);
  }, [sectionValue]);

  const updateValidation = (validationStatus: ValidationStatus, validationComment: string): void => {
    setLoading(true);
    dispatch(
      updateSectionValueValidation({
        sectionId: section.id,
        caseId,
        formId,
        validationStatus,
        validationComment,
      })
    )
      .unwrap()
      .then(() => {
        setLoading(false);
        setSaved(true);
      });
  };

  const handleValidationStatusChange = (event: SelectChangeEvent<unknown>) => {
    const validationStatus = event.target.value as ValidationStatus;
    setValidationStatus(validationStatus);
    updateValidation(validationStatus, validationComment);
  };

  const handleValidationCommentChange = (comment: string) => {
    setValidationComment(comment);
    updateValidation(validationStatus, comment);
  };

  return (
    <TabPanel value="qa">
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <FormControl>
          <InputLabel>Validation Status</InputLabel>
          <PSelect label="Validation Status" value={validationStatus} onChange={handleValidationStatusChange}>
            <MenuItem value={ValidationStatus.Unvalidated}>QA Required</MenuItem>
            <MenuItem value={ValidationStatus.NotApplicable}>QA Not Applicable</MenuItem>
            <MenuItem value={ValidationStatus.Issue}>QA Issue</MenuItem>
            <MenuItem value={ValidationStatus.Approved}>QA Approved</MenuItem>
          </PSelect>
        </FormControl>
        <Commentary
          textarea={
            <DebouncedTextarea
              minRows={1}
              textareaProps={{
                placeholder: "Write comments about the section...",
              }}
              value={validationComment}
              onChange={handleValidationCommentChange}
            />
          }
        />
      </Box>

      <LastEdited
        by={qaUser !== undefined ? fullName(qaUser) : INTERNAL_USER_NAME}
        at={stripNull(sectionValue?.validationUpdatedAt)}
      />
      <Box sx={{ mt: 3, display: "flex" }}>
        <SavedState saved={saved} loading={loading} />
      </Box>
    </TabPanel>
  );
};

const SectionOutcome: FC<SectionOutcomeProps> = ({ caseId, formId, section, sectionValue, tab }) => {
  const navigate = useNavigate();

  const handleTabChange = (_event: SyntheticEvent, newValue: SectionOutcomeTab) => {
    navigate(`?sectionId=${section.id}&section=${newValue}`);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", backgroundColor: "white", width: "100%" }}>
      <TabContext value={tab}>
        <TabList
          onChange={handleTabChange}
          sx={{
            " .MuiTab-root.Mui-selected": {
              color: "grey.900",
            },
            " .MuiTab-root": {
              color: "grey.400",
            },
            " .MuiTabs-indicator": {
              backgroundColor: "grey.900",
              height: 4,
            },
          }}>
          <Tab value="review" sx={{ width: "50%" }} label="Review" data-pendo="case-profile-review-section-tab" />
          {section.validationEnabled && (
            <Tab value="qa" sx={{ width: "50%" }} label="QA" data-pendo="case-profile-QA-section-tab" />
          )}
        </TabList>
        <OutcomeReview caseId={caseId} formId={formId} section={section} sectionValue={sectionValue} />
        <OutcomeQA caseId={caseId} formId={formId} section={section} sectionValue={sectionValue} />
      </TabContext>
    </Box>
  );
};

export default SectionOutcome;
